import { mergeValues } from '~/utils';

// Every Overview section form is seeded with the whole project, not just its own fields: a section saves the
// full payload, so the fields it does not render still have to round-trip their stored values.
export default function getOverviewInitialValues(project, { workspace, features }) {
  const initialValues = mergeValues(
    {
      billingTypeId: 'tm',
      client: null,
      code: '',
      completedOn: null,
      currency: null,
      description: '',
      enableClientApprovals: false,
      end: null,
      isBillable: true,
      isExpensesApprovalRequired: true,
      isTimeApprovalRequired: true,
      lockTimeAndExpenses: true,
      name: '',
      key: '',
      revenueRecognitionMethod: 'invoiced',
      revenueAttributionMethod: 'hours',
      poNumber: '',
      practice: null,
      businessUnit: null,
      projectType: '',
      projectGroup: null,
      purchaseOrder: null,
      salesRepresentative: null,
      start: null,
      statusId: 'not_started',
      slackChannelId: '',
      cloudFolderUrl: '',
      autoAssignMembers: false,
      assignmentNotifications: true,
      tags: [],
      isProductive: false,
      useHealthReports: true,
      defaultExpenseItemIsBillable: false,

      // Time tracking rules
      capMaxMemberHoursPerDay: false,
      maxMemberHoursPerDay: 8,
      capMaxMemberHoursPerWeek: false,
      maxMemberHoursPerWeek: 40,
      capMaxMemberHoursPerMonth: false,
      maxMemberHoursPerMonth: 160,
      forbidTimeOnHolidays: true,
      forbidTimeOnWeekends: true,
      requireNotes: false,
      requireTimeEntryTask: false,
      trackTimeToAssignedRoles: false,
      restrictTimeToAllocatedDays: false,

      // Invoice details
      invoiceDetailsSource: 'client',
      email: '',
      emails: [],
      ccEmail: '',
      ccEmails: [],
      invoicePaymentTerm: null,
      invoiceTaxableItems: [],
      invoiceTaxRate: null,
      streetAddress: '',
      invoiceSubject: '',
      invoiceNotes: '',
      invoiceEmailSubject: '',
      invoiceEmailBody: '',

      // Accounting integration
      accountingConnectionId: null,
      accountingProvider: null,
      accountingNotes: '',
      qboProjectId: '',

      // Section toggles
      useRoles: false,
      useBudget: false,
      useMonthlyBudget: false,

      // Task dependencies
      useDependencies: false,
      autoCompleteMilestones: true,
    },
    project,
  );

  // The QBO mapping is stored as a relation array, not a scalar, so mergeValues can't pick it up. Deriving it
  // here matters beyond display: submit treats an empty value as "no project selected" and deletes the
  // mapping for the current connection.
  initialValues.qboProjectId =
    project.qboProjects?.find((qp) => qp.qboConnectionId === project.accountingConnectionId)?.qboProjectId ?? '';

  // Invoice details are inherited unless the project overrides them, so the form has to show the value that
  // will actually be used — the project's own columns are empty while it defers to a client, business unit or
  // the workspace. Ported from the drawer, which grew this after this tab was first split out of it.
  switch (project.invoiceDetailsSource) {
    case 'client':
      initialValues.emails = project.client.emails;
      initialValues.email = project.client.emails.join(', ');
      initialValues.ccEmails = project.client.ccEmails;
      initialValues.ccEmail = project.client.ccEmails.join(', ');
      initialValues.streetAddress = project.client.streetAddress ?? '';
      switch (project.client.invoiceDetailsSource) {
        case 'workspace':
          initialValues.invoiceSubject = workspace.invoiceSubject ?? '';
          initialValues.invoiceNotes = workspace.invoiceNotes ?? '';
          initialValues.invoiceEmailSubject = workspace.invoiceEmailSubject ?? '';
          initialValues.invoiceEmailBody = workspace.invoiceEmailBody ?? '';
          initialValues.invoicePaymentTerm = workspace.invoicePaymentTerm;
          initialValues.invoiceTaxableItems = workspace.invoiceTaxableItems;
          initialValues.invoiceTaxRate = workspace.invoiceTaxRate;
          break;
        case 'business_unit':
          if (features.businessUnits && project.client.businessUnitId) {
            initialValues.invoiceSubject = project.client.businessUnit.invoiceSubject ?? '';
            initialValues.invoiceNotes = project.client.businessUnit.invoiceNotes ?? '';
            initialValues.invoiceEmailSubject = project.client.businessUnit.invoiceEmailSubject ?? '';
            initialValues.invoiceEmailBody = project.client.businessUnit.invoiceEmailBody ?? '';
            initialValues.invoicePaymentTerm = project.client.businessUnit.invoicePaymentTerm;
            initialValues.invoiceTaxableItems = project.client.businessUnit.invoiceTaxableItems;
            initialValues.invoiceTaxRate = project.client.businessUnit.invoiceTaxRate;
          }
          break;
        case 'custom':
          initialValues.invoiceSubject = project.client.invoiceSubject ?? '';
          initialValues.invoiceNotes = project.client.invoiceNotes ?? '';
          initialValues.invoiceEmailSubject = project.client.invoiceEmailSubject ?? '';
          initialValues.invoiceEmailBody = project.client.invoiceEmailBody ?? '';
          initialValues.invoicePaymentTerm = project.client.invoicePaymentTerm;
          initialValues.invoiceTaxableItems = project.client.invoiceTaxableItems;
          initialValues.invoiceTaxRate = project.client.invoiceTaxRate;
          break;
      }
      break;
    case 'business_unit':
      if (features.businessUnits && project.businessUnitId) {
        initialValues.invoiceSubject = project.businessUnit.invoiceSubject ?? '';
        initialValues.invoiceNotes = project.businessUnit.invoiceNotes ?? '';
        initialValues.invoiceEmailSubject = project.businessUnit.invoiceEmailSubject ?? '';
        initialValues.invoiceEmailBody = project.businessUnit.invoiceEmailBody ?? '';
        initialValues.invoicePaymentTerm = project.businessUnit.invoicePaymentTerm;
        initialValues.invoiceTaxableItems = project.businessUnit.invoiceTaxableItems;
        initialValues.invoiceTaxRate = project.businessUnit.invoiceTaxRate;
      }
      break;
  }

  return initialValues;
}
